import { Component, Input, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectIsBtMasterOnly, selectIsDbsUser, selectIsEmployee, selectSubAccountId } from '../../../account-info-header/reducers';
import { EAuthorizationActions } from '../../../setup-eAuthorization/actions';
import { loadCreateStandingAuthEAuthClients, loadEAuthClients } from '../../../setup-eAuthorization/actions/eAuthorization.actions';
import { EAuthClient, EAuthClientsResponse } from '../../../setup-eAuthorization/models/EAuthClients';
import { selectCreateStandingAuthEAuthClients, selectEAuthClients, selectEauthMissingEmail, selectEmailState } from '../../../setup-eAuthorization/reducers';
import { EAuthActions } from '../../actions';
import { EAuthAccountHolder } from '../../models/EAuthAccountHolder';
import { selectEAuthAccountHolderSelected, selectEAuthEmailAddressError, selectEAuthError, selectEAuthSelectedAccountHolderIds, selectIsCreateStandingAuth } from '../../reducers';
import { TransactionType } from '../../types/Transaction';
import { Constants, EAuthEligibility, eAuthNotifications } from '../../utilities/Constants';
import { AccountInfoActions } from '../../../account-info-header/actions';
import { selectStagedTransactionForm, selectVerifyCallFinished, selectWireProcessDateFields } from '../../../create-request/reducers';
import { WireTransactionDetails } from '../../../create-request/models/form/TransactionForm';
import { selectEAuthInfo } from '../../reducers';
import { getProcessDateInIsoFormat } from '../../../create-request/components/transactions-table/table-helpers';
import { EAuthInfo } from '../../models/EAuthInfo';
import { selectSLOAVerifyCallFinished } from '../../../standing-authorizations/reducers';

@Component({
  standalone: false,
  selector: 'mm-eauth-table',
  templateUrl: './eauth-table.component.html',
  styleUrls: ['./eauth-table.component.scss']
})
export class EauthTableComponent implements OnInit {
  readonly clientEmailTooltipText = Constants.clientEmailTooltipText;
  @Input() showSubmitEmailAddressButton: boolean | undefined | null = false;
  @Input() setupEAuthorizationSelected: boolean | undefined | null = false;
  @Input() standingAuthorizationsEAuth: boolean = false;
  @Input() isInitiateAndReleaseFlowFromTable: boolean = false;
  @Input() isPalAccount: boolean = false;
  isOptedOut: boolean = false;
  accountHolderControl: UntypedFormControl = new UntypedFormControl('');
  accountHolderFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  emailForm = new UntypedFormGroup({
    emailControl: new UntypedFormControl('', [Validators.required, Validators.email])
  });
  eAuthError$: Observable<boolean>;
  eAuthEmailAddressError$: Observable<boolean>;
  eAuthAccountHolders: EAuthAccountHolder[] = [];
  eAuthClientsResponse: EAuthClientsResponse;
  eAuthClients: EAuthClient[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  transactionType: TransactionType | "";
  submitEmailAddressEnabled: boolean = true;
  isEmployee: boolean;
  isDbsUser: boolean;
  subAccountId: string;
  confirmationText = eAuthNotifications.emailSubmitSuccess;
  errorText = eAuthNotifications.emailSubmitFailed;
  invalidEAuthMessage = eAuthNotifications.inValidEAuthStandingAuth;
  missingEmailMessage = eAuthNotifications.missingEmailMessage;
  containsIneligibleAccountHolderStandingAuthFlow: boolean;
  showEmailSubmitSuccessNotification: boolean = false;
  showEmailSubmitErrorNotification: boolean = false;
  attemptedSubmitEmailCustomerId: string;
  showMissingEmailMessage$: Observable<boolean>;
  isCreateStandingAuth: boolean = false;
  showWireExtendedProcessDateMesssage: boolean = false;
  isBtMasterOnly: boolean = false;
  isaccountHolderSelected: boolean = false;
  eauthInfoDetails: EAuthInfo;

  constructor(private store: Store) { }

  ngOnInit(): void {
    if (this.isInitiateAndReleaseFlowFromTable) this.emailForm?.get('emailControl')?.disable();

    this.store.select(selectIsEmployee).pipe(takeUntil(this.destroy$)).subscribe(isEmployee => this.isEmployee = isEmployee);
    this.store.select(selectIsDbsUser).pipe(takeUntil(this.destroy$)).subscribe(isDbsUser => this.isDbsUser = isDbsUser);
    this.eAuthError$ = this.store.select(selectEAuthError);
    this.showMissingEmailMessage$ = this.store.select(selectEauthMissingEmail).pipe(takeUntil(this.destroy$));
    this.eAuthEmailAddressError$ = this.store.select(selectEAuthEmailAddressError);
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => this.subAccountId = subAccountId);
    this.store.select(selectIsCreateStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(createType => this.isCreateStandingAuth = createType);

    this.store.select(selectIsBtMasterOnly).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.isBtMasterOnly = val;
    });

    if ((this.isDbsUser || this.isEmployee || this.isBtMasterOnly) && !this.isInitiateAndReleaseFlowFromTable) {
      this.submitEmailAddressEnabled = false;
    }

    this.store.select(selectEAuthSelectedAccountHolderIds).subscribe(clients => {
      if (clients && !clients.includes(this.attemptedSubmitEmailCustomerId) && this.showEmailSubmitErrorNotification === true) {
        this.showEmailSubmitErrorNotification = false;
      }
    });

    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectWireProcessDateFields),
      this.store.select(selectEAuthInfo)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, processDateFields, eAuthInfo]) => {
      this.eauthInfoDetails = eAuthInfo;
      if (form.transactionType === 'wire' && eAuthInfo.isOptedOut) {
        var extendedDate = new Date(getProcessDateInIsoFormat(processDateFields.extendedProcessDates[0]));
        var selectedDate = new Date(getProcessDateInIsoFormat((form.transactionDetails as WireTransactionDetails).selectedDate));
        if (processDateFields.isExtendedProcessingWindow && extendedDate.toDateString() === selectedDate.toDateString()) {
          this.showWireExtendedProcessDateMesssage = true;
        } else {
          this.showWireExtendedProcessDateMesssage = false;
        }
      } else {
        this.showWireExtendedProcessDateMesssage = false;
      }
    });

    combineLatest([
      this.store.select(selectEAuthClients),
      this.store.select(selectCreateStandingAuthEAuthClients)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([eAuthClientsResponse, createStandingAuthEauthClientsResponse]) => {
      this.eAuthClientsResponse = this.standingAuthorizationsEAuth ? createStandingAuthEauthClientsResponse : eAuthClientsResponse;
      this.eAuthClients = this.eAuthClientsResponse.eauthClients;
      this.eAuthAccountHolders = this.mapEauthClientsToAccountHolders(this.eAuthClients);

      if (!this.isInitiateAndReleaseFlowFromTable) {
          if (this.eAuthClientsResponse.isAccountRestricted || this.cannotDisplayAnyAccountHolders()) 
          {
            this.isOptedOut = true;
            this.store.dispatch(EAuthActions.optOut());
          }
          else 
          {
            this.isOptedOut = false;
            this.store.dispatch(EAuthActions.optIn());
          }
      }
      else if (this.standingAuthorizationsEAuth) {
        this.addAllFormControl();
      }
    });

    combineLatest([
      this.store.select(selectEAuthAccountHolderSelected),
      this.store.select(selectVerifyCallFinished),
      this.store.select(selectSLOAVerifyCallFinished)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isAccountHolderSelected, verifyCallFinished, sloaVerifyCallFinsished]) => {
      if ((verifyCallFinished || sloaVerifyCallFinsished) && this.isInitiateAndReleaseFlowFromTable) {
        this.isaccountHolderSelected = isAccountHolderSelected;
        this.isEauthCustomerSelectedForInitiateAndReleaseTable();
      }
    });
  }

  addAllFormControl() {
    this.eAuthAccountHolders.forEach((val) => {
      this.accountHolderFormGroup.setControl(`${val.name}-${val.id}`, new FormControl({ value: !this.containsIneligibleAccountHolderStandingAuthFlow, disabled: this.isOptedOut || this.eAuthClientsResponse.isAccountRestricted || !val.isEauthEligible || this.isInitiateAndReleaseFlowFromTable }));
    });
  }

  onAccountHolderSelectedStandingAuth(target: any, accountId: string) {
    this.setAllFormControls(target?.checked);
    this.onOptOutChange();
  }

  setAllFormControls(value: boolean) {
    Object.keys(this.accountHolderFormGroup.controls).forEach(element => {
      let currAccountHolder = this.eAuthAccountHolders.find((accountHolder) => {
        return element.split('-').filter(acctHolderId => acctHolderId == accountHolder.id);
      });
      this.accountHolderFormGroup.get(element)?.setValue(value, { emitEvent: false });
      if (!currAccountHolder?.isEauthEligible || this.isOptedOut || this.eAuthClientsResponse.isAccountRestricted) {
        this.accountHolderFormGroup.get(element)?.disable();
      }
      else {
        this.accountHolderFormGroup.get(element)?.enable()
      }
    });
  }

  onOptOutChange(): void {
    this.isOptedOut = !this.isOptedOut;
    if (this.isOptedOut) {
      this.store.dispatch(EAuthActions.optOut());
      this.showEmailSubmitErrorNotification = false;
    }
    else {
      this.store.dispatch(EAuthActions.optIn());
    }

    if (this.standingAuthorizationsEAuth && !this.containsIneligibleAccountHolderStandingAuthFlow) {
      let accountHoldersSelected = this.isOptedOut ? [] : [this.accountHolderControl.value];
      this.store.dispatch(EAuthActions.accountHolderSelected({ accountHolder: accountHoldersSelected }));
    }
    this.setAllFormControls(false);
  }

  get allAccountHoldersId(): string[] {
    return this.eAuthAccountHolders.map(val => val.id);
  }

  onAccountHolderSelected(): void {
    this.store.dispatch(EAuthActions.accountHolderSelected({ accountHolder: [this.accountHolderControl.value] }));
  }

  onSubmitEmailAddress(accountHolderId: string): void {
    let index = this.eAuthAccountHolders.findIndex(x => x.id === accountHolderId);
    if (this.emailForm.valid) {
      this.eAuthAccountHolders[index].showSubmitEmailError = false;
      const newEmailAddress = this.emailAddress?.value;
      this.attemptedSubmitEmailCustomerId = this.eAuthAccountHolders[index].id;
      this.store.dispatch(EAuthorizationActions.submitEmailClicked({ accountId: this.subAccountId, customerId: accountHolderId, emailAddress: newEmailAddress }));
      this.store.select(selectEmailState).pipe(takeUntil(this.destroy$)).subscribe(submitEmail => {
        if (submitEmail.isEmailSubmitted) {
          //reload the eauth clients in order to display the updated email
          if (this.standingAuthorizationsEAuth) {
            this.store.dispatch(loadCreateStandingAuthEAuthClients({ accountId: this.subAccountId }));
          } else {
            this.store.dispatch(loadEAuthClients({ accountId: this.subAccountId }));
          }
          // reload AccountInfo eauth data
          this.store.dispatch(AccountInfoActions.refresh({ accountId: this.subAccountId }));
          this.showEmailSubmitSuccessNotification = true;
          this.showEmailSubmitErrorNotification = false;
          this.emailForm.reset();
        }
        else if (submitEmail.isEmailSubmitCallFinished) {
          this.setAllFormControls(false);
          if (submitEmail.hasSendEmailError) {
            this.confirmationText = eAuthNotifications.sendAllianceInstructionsFailed;
            this.showEmailSubmitSuccessNotification = true;
            this.emailForm.reset();
          }
          else {
            this.showEmailSubmitErrorNotification = true;
          }
        }
      });
    }
    else {
      this.eAuthAccountHolders[index].showSubmitEmailError = true;
    }
  }

  cannotDisplayAnyAccountHolders(): boolean {
    if (this.eAuthClients === undefined || this.eAuthClients.length === 0) {
      return false;
    }
    for (var i = 0; i < this.eAuthClients.length; i++) {
      if (this.eAuthClients[i].displayIndicator) {
        return false;
      }
    }
    return true;
  }

  get disableOptOutCheckbox(): boolean {
    return (
      this.eAuthClientsResponse.isAccountRestricted ||
      this.cannotDisplayAnyAccountHolders() ||
      this.containsIneligibleAccountHolderStandingAuthFlow
    );
  }

  get emailAddress() {
    return this.emailForm.get('emailControl');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  mapEauthClientsToAccountHolders(eauthClients: EAuthClient[]): EAuthAccountHolder[] {
    return eauthClients.map(customer => {
      return {
        name: customer.fullName,
        email: customer.emailAddress,
        eligibility: customer.eauthSetup.status as EAuthEligibility,
        eligibilityDetails: customer.eauthSetup.description,
        id: customer.customerId,
        showSubmitEmailError: false,
        isEauthEligible: customer.isEauthEligible
      };
    })
  }

  displayTooltip(accountHolder: EAuthAccountHolder): boolean {
    return accountHolder.eligibility !== "Complete";
  }

  isEauthCustomerSelectedForInitiateAndReleaseTable() {
    if (this.isaccountHolderSelected && this.eauthInfoDetails.selectedAccountHolder.length > 0) {
      this.eauthInfoDetails.selectedAccountHolder.map(customerId => {
        this.accountHolderControl.setValue(customerId);
      });
      this.isOptedOut = false;
    }
    else if (!this.isaccountHolderSelected) {
      this.isOptedOut = true;
      this.setAllFormControls(false);
    }
  }
}
